/** Níveis permitidos na escala (número de estrelas). */
export const RATING_LEVELS_MIN = 2;
export const RATING_LEVELS_MAX = 5;
export const RATING_LEVELS_DEFAULT = 5;

export function clampRatingLevels(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return RATING_LEVELS_DEFAULT;
  return Math.min(RATING_LEVELS_MAX, Math.max(RATING_LEVELS_MIN, Math.round(n)));
}

function snapRatingToScale(n: number, max: number): number | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  const step = 0.5;
  const snapped = Math.round(n / step) * step;
  if (snapped < step) return null;
  if (snapped > max) return max;
  return snapped;
}

export function normalizeRatingValue(
  raw: unknown,
  maxLevels: number
): number | null {
  const max = clampRatingLevels(maxLevels);
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return snapRatingToScale(raw, max);
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t.length) return null;
    /** Quick view / lista envia `"3/5"` ou `"2,5/5"` — `Number("3/5")` é NaN. */
    if (/\//.test(t)) {
      return parseRatingQuickViewDisplay(t, max);
    }
    const n = Number(t.replace(',', '.'));
    return snapRatingToScale(n, max);
  }
  return null;
}

export type StarVisualState = 'empty' | 'half' | 'full';

export function starVisualState(starIndex1Based: number, value: number | null): StarVisualState {
  if (value == null || value <= 0) return 'empty';
  const s = starIndex1Based;
  if (value >= s) return 'full';
  if (value >= s - 0.5) return 'half';
  return 'empty';
}

export function formatRatingForQuickView(value: number | null, maxLevels: number): string {
  const max = clampRatingLevels(maxLevels);
  const v = normalizeRatingValue(value, max);
  if (v == null) return '—';
  const dec = Number.isInteger(v) ? String(v) : v.toFixed(1).replace('.', ',');
  return `${dec}/${max}`;
}

/**
 * Interpreta texto vindo da quick view (`"3/5"`, `"3,5/5"`) ou valor numérico bruto.
 * Usado na list view onde `quickFields` sobrescreve o mapa com string formatada.
 */
export function parseRatingQuickViewDisplay(raw: string, maxLevels: number): number | null {
  const max = clampRatingLevels(maxLevels);
  const t = raw.trim();
  if (!t || t === '—' || t === '-') return null;
  const slash = /^(\d+(?:[.,]\d+)?)\s*\/\s*(\d+)\s*$/i.exec(t);
  if (slash) {
    const num = Number(slash[1].replace(',', '.'));
    if (!Number.isFinite(num)) return null;
    return snapRatingToScale(num, max);
  }
  return normalizeRatingValue(t.replace(',', '.'), max);
}
